/* @flow */

import uniq from 'lodash/uniq';
import get from 'lodash/get';
import { createSelector } from 'reselect';
import { route } from 'preact-router';
import invariant from 'invariant';
import isValidCnpj from '@brazilian-utils/is-valid-cnpj';
import isValidCpf from '@brazilian-utils/is-valid-cpf';
import { setPedidoFinalizadoThunk } from './finalizacao';
import { buscarDados, getProdutos } from './grid-produtos';
import modalConfirmacao from '../../utils/modal-confirmacao';
import sleep from '../../utils/sleep';
import * as Loading from '../loading';
import { MOEDA } from '../../utils/constants';
import buscarVoucherPorCodigo from '../../vouchers/buscar-voucher-por-codigo';
import converterProdutoParaItem from '../../produtos/converter-produto-para-item';
import { getFormasPagamento } from '../empresa';
import isStringDinheiro from '../../formas-pagamento/is-dinheiro';
import { ETAPA_EXECUCAO } from '../../pedidos/constants';
import getElementoPorId from '../../utils/get-elemento-por-id';
import {
    getValorItens,
    getItens,
    EDITAR_ITEM,
    ADICIONAR_ITEM,
    DELETAR_ITEM,
    type AdicionarItemAction,
    type EditarItemAction,
    type DeletarItemAction,
} from './itens';
import { type Pedido } from '../../pedidos/types';
import { type PedidoState } from './types';
import { type State, type GetState } from '../../preact-app/types';
import { type ProdutoPedido, type Produto } from '../../produtos/types';
import { type Voucher } from '../../vouchers/types';
import { type FormaPagamento } from '../../formas-pagamento/types';
import { type EtapaLoading } from '../loading-types';
import api from '../../utils/api';
import formatarReal from '../../utils/formatar-real';
import alertas from '../../alertas';
import formatarRealOuNull from '../../utils/formatar-real-ou-null';
import { AUTENTICAR, type AutenticarAction } from '../usuario';
import buscarVouchersUsuario from '../../vouchers/buscar-vouchers-usuario';
import { toastError, toastSuccess } from '../../toasts';
import dialogoSelecaoVoucher from '../../vouchers/dialogo-selecao-voucher';

type SetIdAction = {
    type: 'balcao/pedido/SET_ID',
    id: number,
};

export type LimparPedidoAction = {
    type: 'balcao/pedido/LIMPAR_PEDIDO',
};

export type SetCpfNotaAction = {
    type: 'balcao/pedido/SET_CPF_NOTA',
    cpfNota: string,
};

type SetVoucherAction = {
    type: 'balcao/pedido/SET_VOUCHER',
    voucher: ?string,
    valorVoucher: ?number,
};

type SetFormasPagamentoIdAction = {
    type: 'balcao/pedido/SET_FORMAS_PAGAMENTO_ID',
    formasPagamentoId: ?number,
};

type SetTrocoAction = {
    type: 'balcao/pedido/SET_TROCO',
    troco: number,
};

type ProdutoAdicionadoComSucessoAction = {
    type: 'balcao/pedido/PRODUTO_PROCESSADO_COM_SUCESSO',
    id: number,
};

export type CarregarPedidoAction = {
    type: 'balcao/pedido/CARREGAR_PEDIDO',
    pedido: Pedido,
    produtos: ProdutoPedido[],
};

type SetProdutoAdicionadoAction = {
    type: 'balcao/pedido/SET_PRODUTO_ADICIONADO',
    id: number,
};

export type PedidoAction =
    | SetIdAction
    | CarregarPedidoAction
    | ProdutoAdicionadoComSucessoAction
    | LimparPedidoAction
    | SetFormasPagamentoIdAction
    | SetTrocoAction
    | SetVoucherAction
    | AdicionarItemAction
    | DeletarItemAction
    | EditarItemAction
    | SetProdutoAdicionadoAction
    | AutenticarAction
    | SetCpfNotaAction;

// Action Types

export const LIMPAR_PEDIDO = 'balcao/pedido/LIMPAR_PEDIDO';
export const CARREGAR_PEDIDO = 'balcao/pedido/CARREGAR_PEDIDO';
const SET_CPF_NOTA = 'balcao/pedido/SET_CPF_NOTA';
const SET_FORMAS_PAGAMENTO_ID = 'balcao/pedido/SET_FORMAS_PAGAMENTO_ID';
const SET_TROCO = 'balcao/pedido/SET_TROCO';
const SET_VOUCHER = 'balcao/pedido/SET_VOUCHER';
const SET_ID = 'balcao/pedido/SET_ID';
const PRODUTO_PROCESSADO_COM_SUCESSO = 'balcao/pedido/PRODUTO_PROCESSADO_COM_SUCESSO';
const SET_PRODUTO_ADICIONADO = 'balcao/pedido/SET_PRODUTO_ADICIONADO';

// Loading

const LOADING_CRIANDO_PEDIDO = 'LOADING_CRIANDO_PEDIDO';
const LOADING_ADICIONANDO_PRODUTOS = 'LOADING_ADICIONANDO_PRODUTOS';
const LOADING_ALTERANDO_ETAPA = 'LOADING_ALTERANDO_ETAPAS';
const LOADING_FINALIZACAO = [
    { nome: LOADING_CRIANDO_PEDIDO, mensagem: 'Criando pedido' },
    { nome: LOADING_ADICIONANDO_PRODUTOS, mensagem: 'Adicionando produtos' },
    { nome: LOADING_ALTERANDO_ETAPA, mensagem: 'Alterando etapa' },
];

// Action Creators

export const limparPedido = (): LimparPedidoAction => ({
    type: LIMPAR_PEDIDO,
});

const setId = (id: number): SetIdAction => ({
    type: SET_ID,
    id,
});

const produtoProcessadoComSucesso = (id: number): ProdutoAdicionadoComSucessoAction => ({
    type: PRODUTO_PROCESSADO_COM_SUCESSO,
    id,
});

export const setCpfNota = (cpfNota: string): SetCpfNotaAction => ({
    type: SET_CPF_NOTA,
    cpfNota,
});

export const setTroco = (troco: number): SetTrocoAction => ({
    type: SET_TROCO,
    troco,
});

const setVoucher = (voucher: ?string, valorVoucher: ?number): SetVoucherAction => ({
    type: SET_VOUCHER,
    voucher,
    valorVoucher,
});

const calcularValorVoucher = (voucher: Voucher, valorItens: number) => {
    if (!voucher) {
        return 0;
    }

    if (voucher.tipo === MOEDA) {
        return voucher.valor;
    }

    return valorItens * voucher.valor / 100;
};

export const setFormasPagamentoId = (formasPagamentoId: number): SetFormasPagamentoIdAction => ({
    type: SET_FORMAS_PAGAMENTO_ID,
    formasPagamentoId: Number.isNaN(formasPagamentoId) ? null : formasPagamentoId,
});

export const carregarPedido = (
    pedido: Pedido,
    produtos: ProdutoPedido[],
): CarregarPedidoAction => ({
    type: CARREGAR_PEDIDO,
    pedido,
    produtos,
});

const setProdutoAdicionado = (id: number): SetProdutoAdicionadoAction => ({
    type: SET_PRODUTO_ADICIONADO,
    id,
});

const editandoPedido = () => Loading.loading(LOADING_CRIANDO_PEDIDO, 'Editando pedido...');
const pedidoEditadoComSucesso = () => Loading.success(LOADING_CRIANDO_PEDIDO, 'Pedido editado com sucesso.');
const naoFoiPossivelEditarPedido = (err: Error) => {
    const mensagem = get(err, 'response.data.message', 'Não foi possível editar o pedido.');
    return Loading.error(LOADING_CRIANDO_PEDIDO, mensagem);
};

const criandoPedido = () => Loading.loading(LOADING_CRIANDO_PEDIDO);
const pedidoCriadoComSucesso = () => Loading.success(LOADING_CRIANDO_PEDIDO);
const naoFoiPossivelCriarPedido = (err: Error) => {
    const mensagem = get(err, 'response.data.message', 'Não foi possível criar o pedido.');
    return Loading.error(LOADING_CRIANDO_PEDIDO, mensagem);
};

const adicionandoProdutos = () => Loading.loading(LOADING_ADICIONANDO_PRODUTOS);
const produtosAdicionadosComSucesso = () => Loading.success(LOADING_ADICIONANDO_PRODUTOS);

const naoFoiPossivelAdicionarOsProdutos = (err: Error) => {
    const mensagem = get(err, 'response.data.message', 'Não foi possível adicionar os produtos.');
    return Loading.error(LOADING_ADICIONANDO_PRODUTOS, mensagem);
};

const alterandoEtapa = () => Loading.loading(LOADING_ALTERANDO_ETAPA);
const etapaAlteradaComSucesso = () => Loading.success(LOADING_ALTERANDO_ETAPA);
const erroAoAlterarEtapa = (err: Error) => {
    const mensagem = get(err, 'response.data.message', 'Não foi possível alterar etapas.');
    return Loading.error(LOADING_ALTERANDO_ETAPA, mensagem);
};

// Selectors

export const getCliente = (state: State) => state.balcao.cliente;
export const getEndereco = (state: State) => state.balcao.endereco;
export const getPedido = (state: State) => state.balcao.pedido;
const getId = (state: State) => getPedido(state).id;
const getEtapasId = (state: State) => getPedido(state).etapasId;
export const getFormasPagamentoId = (state: State) => getPedido(state).formasPagamentoId;
export const getTrocoPara = (state: State) => getPedido(state).troco;
export const getCpfNota = (state: State) => getPedido(state).cpfNota;
export const getVoucher = (state: State) => getPedido(state).voucher;
export const getValorVoucher = (state: State) => getPedido(state).valorVoucher || 0;
export const getTaxaEntrega = (state: State) => {
    const endereco = getEndereco(state);
    return endereco ? endereco.valor : 0;
};
export const getClientesId = (state: State) => {
    const cliente = getCliente(state);
    return cliente ? cliente.id : null;
};

export const isCpfNotaValido: State => boolean = createSelector(
    getCpfNota,
    cpfNota => !cpfNota || isValidCpf(cpfNota) || isValidCnpj(cpfNota),
);

export const getFormaPagamentoSelecionada: State => ?FormaPagamento = createSelector(
    getFormasPagamento,
    getFormasPagamentoId,
    (formasPagamento, formasPagamentoId) => (
        formasPagamentoId ? formasPagamento.find(({ id }) => id === formasPagamentoId) : null
    ),
);

export const isDinheiro: State => boolean = createSelector(
    getFormaPagamentoSelecionada,
    forma => !!forma && isStringDinheiro(forma.nome),
);

export const getValorTotal: State => number = createSelector(
    [getValorVoucher, getValorItens, getTaxaEntrega],
    (valorVoucher, valorItens, taxaEntrega) => valorItens + taxaEntrega - valorVoucher,
);

const getValorParaLevar: State => number = createSelector(
    [getValorTotal, getTrocoPara],
    (valorTotal, trocoPara) => trocoPara - valorTotal,
);

export const getValorVoucherFormatado: State => ?string = createSelector(
    getValorVoucher,
    formatarRealOuNull,
);

export const getValorParaLevarFormatado: State => ?string = createSelector(
    getValorParaLevar,
    formatarRealOuNull,
);

export const getValorTotalFormatado: State => string = createSelector(
    getValorTotal,
    formatarReal,
);

export const getTaxaEntregaFormatado: State => ?string = createSelector(
    getTaxaEntrega,
    formatarRealOuNull,
);

export const getLoadingFinalizacao: State => EtapaLoading[] = createSelector(
    Loading.getLoading,
    loading => loading.filter(({ nome }) => [
        LOADING_CRIANDO_PEDIDO,
        LOADING_ADICIONANDO_PRODUTOS,
        LOADING_ALTERANDO_ETAPA,
    ].includes(nome)),
);

export const isTrocoValido: State => boolean = createSelector(
    [getValorTotal, getTrocoPara],
    (valorTotal, troco) => (troco === 0 || troco > valorTotal),
);

const getPedidoPost = createSelector(
    [
        getPedido,
        getEndereco,
        getCliente,
        getTaxaEntrega,
        getValorTotal,
    ],
    (pedido, endereco, cliente, taxaEntrega, valor) => {
        const storage = JSON.parse(localStorage.getItem('preact-app-state'));
        const unidadeSelecionada = storage.empresa.unidadesSelecionadas[0];
        invariant(endereco && cliente, 'Obrigatório');

        return {
            cpfNota: pedido.cpfNota,
            voucher: pedido.voucher,
            valorVoucher: pedido.valorVoucher,
            troco: pedido.troco,
            formasPagamentoId: pedido.formasPagamentoId,
            enderecosId: endereco.id,
            usuariosId: cliente.id,
            taxaEntrega,
            unidadesNegocioId: unidadeSelecionada,
            valor: valor + pedido.valorVoucher,
        };
    },
);

const getProdutosParaAdicionar = createSelector(
    [getItens, s => getPedido(s).produtosParaAdicionar],
    (itens, ids) => itens.filter(({ id }) => ids.includes(id)),
);

const getProdutosParaEditar = createSelector(
    [getItens, s => getPedido(s).produtosParaEditar],
    (itens, ids) => itens.filter(({ id }) => ids.includes(id)),
);

const getProdutosParaDeletar = (state: State) => getPedido(state).produtosParaDeletar;

export const getPodeFinalizar = (state: State) => isTrocoValido(state)
    && isCpfNotaValido(state)
    && getFormasPagamentoId(state)
    && getCliente(state)
    && getEndereco(state)
    && getItens(state).length > 0;

// Utils

const buscarPedido = (id: number): Promise<[Pedido, ProdutoPedido[]]> => api.getPedidoPorId(id)
    .then(({ data: pedido }) => Promise.all([
        pedido,
        (api.getProdutosPorPedidosId(pedido.id).then(response => response.data)),
    ]));

const makeProdutoParaAdicionar = (produto: ProdutoPedido) => ({
    ...produto,
    id: null,
    complementos: produto.complementos.map(complemento => ({
        ...complemento,
        id: null,
    })),
});

const alertaSelecioneUmCliente = () => {
    alertas.alertarFinalizacao('Selecione um cliente!')
        .then(() => getElementoPorId('botao-selecionar-cliente-balcao').focus());
};

const getPodeFinalizarAlertando = (state: State): boolean => {
    if (!getItens(state).length) {
        alertas.alertarFinalizacao('Adicione um produto!')
            .then(() => getElementoPorId('pesquisa-produto-balcao').focus());
        return false;
    }

    if (!getCliente(state)) {
        alertaSelecioneUmCliente();
        return false;
    }

    if (!getEndereco(state)) {
        alertas.alertarFinalizacao('Selecione um endereço!')
            .then(() => getElementoPorId('botao-selecionar-endereco-balcao').focus());
        return false;
    }

    if (!getFormasPagamentoId(state)) {
        alertas.alertarFinalizacao('Selecione uma forma de pagamento!')
            .then(() => getElementoPorId('formas-pagamento-balcao').focus());
        return false;
    }

    if (!isCpfNotaValido(state)) {
        getElementoPorId('cpf-nota-balcao').focus();
        return false;
    }

    return true;
};

// Thunk

const aplicarVoucher = (voucher: Voucher) => (dispatch: Function, getState: GetState) => {
    const valorItens = getValorItens(getState());
    if (valorItens <= voucher.valorMinimo) {
        toastSuccess.fire(`O pedido não atingiu o valor mininmo de ${valorItens} para aplicar o cupom.`);
        return;
    }

    dispatch(setVoucher(voucher.codigo, calcularValorVoucher(voucher, valorItens)));
    toastSuccess.fire(`Cupom de desconto "${voucher.codigo}" aplicado.`);
};

export const buscarVoucher = (codigo: string) => (dispatch: Function) => {
    if (!codigo) {
        dispatch(setVoucher());
        toastError.fire('Cupom de desconto removido.');
        return;
    }

    buscarVoucherPorCodigo(codigo)
        .then(({ data: voucher }) => dispatch(aplicarVoucher(voucher))).catch(() => {
            toastError.fire(`Cupom de desconto ${codigo} não existe.`);
            dispatch(setVoucher());
        });
};

export const listarVouchersDoUsuario = () => (dispatch: Function, getState: GetState) => {
    const usuariosId = getClientesId(getState());
    if (!usuariosId) {
        alertaSelecioneUmCliente();
        return;
    }

    buscarVouchersUsuario(usuariosId).then(({ data: vouchers }) => {
        if (!vouchers.length) {
            toastError.fire('Nenhum cupom disponível para este usuário.');
            window.location.hash = '';
            return;
        }

        dialogoSelecaoVoucher(vouchers).then((voucher) => {
            if (voucher) {
                dispatch(aplicarVoucher(voucher));
            }
        });
    });
};

const popularItens = (itens: ProdutoPedido[], produtos: Produto[]) => (
    itens.map((item) => {
        const produto = produtos.find(({ id }) => id === item.produtosId);
        if (!produto) {
            return null;
        }
        return converterProdutoParaItem(produto, {
            id: item.id,
            quantidade: item.quantidade,
            observacao: item.observacao,
            complementos: item.complementos,
        });
    }).filter(Boolean)
);

export const carregarPedidoPorId = (id: number) => (dispatch: Function, getState: GetState) => {
    dispatch(limparPedido());
    return dispatch(buscarDados())
        .then(() => buscarPedido(id))
        .then(([pedido, itens]) => [pedido, popularItens(itens, getProdutos(getState()))])
        .then(([pedido, itens]) => dispatch(carregarPedido(pedido, itens)))
        .then(() => route('/balcao/produtos', true));
};

export const limparPedidoComConfirmacao = () => (dispatch: Function) => {
    modalConfirmacao({
        titulo: 'Limpar o pedido',
        texto: 'O cliente, o endereço e todos itens adicionados no pedido atual serão removidos.',
        sim: 'Limpar',
        nao: 'Cancelar',
        focarNoConfirmar: true,
    }).then(() => {
        dispatch(limparPedido());
        route('/balcao');
    });
};

const adicionarProduto = (pedidosId: number, item: ProdutoPedido) => (dispatch: Function) => (
    api.adicionarProdutoAoPedido(pedidosId, makeProdutoParaAdicionar(item))
        .then(() => dispatch(produtoProcessadoComSucesso(item.id)))
        .then(() => dispatch(setProdutoAdicionado(item.id)))
);

const editarProduto = (pedidosId: number, item: ProdutoPedido) => (dispatch: Function) => (
    api.editarProdutoDoPedido(pedidosId, item.id, makeProdutoParaAdicionar(item))
        .then(() => dispatch(produtoProcessadoComSucesso(item.id)))
);

const deletarProduto = (pedidosId: number, produtosId: number) => (dispatch: Function) => (
    api.deletarProdutoDoPedido(pedidosId, produtosId)
        .then(() => dispatch(produtoProcessadoComSucesso(produtosId)))
);

const sincronizarPedido = () => (dispatch: Function, getState: GetState): Promise<number> => {
    const pedidoPost = getPedidoPost(getState());
    const id = getId(getState());
    if (id) {
        dispatch(editandoPedido());
        return api.editarPedido(id, pedidoPost)
            .then(() => {
                dispatch(pedidoEditadoComSucesso());
                return id;
            })
            .catch((err) => {
                dispatch(naoFoiPossivelEditarPedido(err));
                return Promise.reject();
            });
    }

    dispatch(criandoPedido());
    return api.criarPedido(pedidoPost)
        .then(({ data: { id: pedidosId } }) => {
            dispatch(setId(pedidosId));
            dispatch(pedidoCriadoComSucesso());
            return pedidosId;
        })
        .catch((err) => {
            dispatch(naoFoiPossivelCriarPedido(err));
            return Promise.reject();
        });
};

const adicionarProdutos = (
    pedidosId: number,
) => (
    dispatch: Function,
    getState: GetState,
): Promise<number> => {
    const produtosParaAdicionar = getProdutosParaAdicionar(getState());
    const produtosParaEditar = getProdutosParaEditar(getState());
    const produtosParaDeletar = getProdutosParaDeletar(getState());

    dispatch(adicionandoProdutos());
    return Promise.all(
        []
            .concat(produtosParaAdicionar.map(item => dispatch(adicionarProduto(pedidosId, item))))
            .concat(produtosParaEditar.map(item => dispatch(editarProduto(pedidosId, item))))
            .concat(produtosParaDeletar.map(codigo => dispatch(deletarProduto(pedidosId, codigo)))),
    )
        .then(() => {
            dispatch(produtosAdicionadosComSucesso());
            return pedidosId;
        })
        .catch((err) => {
            dispatch(naoFoiPossivelAdicionarOsProdutos(err));
            return Promise.reject();
        });
};

const alterarEtapa = (id: number) => (dispatch: Function, getState: GetState): Promise<void> => {
    dispatch(alterandoEtapa());

    if (getEtapasId(getState())) {
        dispatch(etapaAlteradaComSucesso());
        return Promise.resolve();
    }

    return api.editarPedido(id, { etapasId: ETAPA_EXECUCAO })
        .then(() => {
            dispatch(etapaAlteradaComSucesso());
        })
        .catch((err) => {
            dispatch(erroAoAlterarEtapa(err));
            return Promise.reject();
        });
};

export const finalizarVenda = () => (dispatch: Function, getState: GetState) => {
    if (!getPodeFinalizarAlertando(getState())) {
        return Promise.resolve();
    }

    dispatch(Loading.init(LOADING_FINALIZACAO));

    return dispatch(sincronizarPedido())
        .then(id => dispatch(adicionarProdutos(id)))
        .then(id => dispatch(alterarEtapa(id)))
        .then(() => sleep(1250))
        .then(() => {
            dispatch(Loading.destroy());
            dispatch(setPedidoFinalizadoThunk());
            route('/balcao/finalizacao', true);
            global.scrollTo(0, 0);
            dispatch(limparPedido());
        })
        .catch(() => {
            sleep(5000).then(() => dispatch(Loading.destroy()));
        });
};

const initialState = {
    id: null,
    etapasId: null,
    cpfNota: null,
    formasPagamentoId: null,
    troco: 0,
    voucher: '',
    valorVoucher: 0,
    loading: [],
    produtosParaAdicionar: [],
    produtosParaEditar: [],
    produtosParaDeletar: [],
    produtosAdicionados: [],
};

const pedidoReducer = (state: PedidoState = initialState, action: PedidoAction): PedidoState => {
    switch (action.type) {
    case SET_CPF_NOTA:
        return {
            ...state,
            cpfNota: action.cpfNota,
        };
    case SET_FORMAS_PAGAMENTO_ID:
        return {
            ...state,
            troco: 0,
            formasPagamentoId: action.formasPagamentoId,
        };
    case SET_TROCO:
        return {
            ...state,
            troco: action.troco,
        };
    case SET_VOUCHER:
        return {
            ...state,
            valorVoucher: action.valorVoucher,
            voucher: action.voucher,
        };
    case SET_ID:
        return {
            ...state,
            id: action.id,
        };
    case SET_PRODUTO_ADICIONADO:
        return {
            ...state,
            produtosAdicionados: uniq(state.produtosAdicionados.concat(action.id)),
        };
    case PRODUTO_PROCESSADO_COM_SUCESSO: {
        const { id } = action;
        return {
            ...state,
            produtosParaAdicionar: state.produtosParaAdicionar.filter(x => x !== id),
            produtosParaEditar: state.produtosParaEditar.filter(x => x !== id),
            produtosParaDeletar: state.produtosParaDeletar.filter(x => x !== id),
        };
    }
    case ADICIONAR_ITEM: {
        const { id } = action.item;
        if (state.produtosAdicionados.includes(id)) {
            return state;
        }
        return {
            ...state,
            produtosParaAdicionar: uniq(state.produtosParaAdicionar.concat(id)),
        };
    }
    case EDITAR_ITEM: {
        const { id } = action;
        if (!state.produtosAdicionados.includes(id)) {
            return state;
        }

        return {
            ...state,
            produtosParaEditar: uniq(state.produtosParaEditar.concat(id)),
        };
    }
    case DELETAR_ITEM: {
        const { id } = action;
        if (!state.produtosAdicionados.includes(id)) {
            return {
                ...state,
                produtosParaAdicionar: state.produtosParaAdicionar.filter(x => x !== id),
            };
        }

        if (!state.produtosParaEditar.includes(id)) {
            return {
                ...state,
                produtosParaEditar: state.produtosParaEditar.filter(x => x !== id),
                produtosParaDeletar: uniq(state.produtosParaDeletar.concat(id)),
            };
        }
        return {
            ...state,
            produtosParaAdicionar: state.produtosParaAdicionar.filter(x => x !== id),
        };
    }
    case LIMPAR_PEDIDO:
    case AUTENTICAR:
        return initialState;
    case CARREGAR_PEDIDO: {
        const { pedido, produtos } = action;
        return {
            ...state,
            id: pedido.id,
            etapasId: pedido.etapa.etapasId,
            troco: pedido.troco || 0,
            cpfNota: pedido.cpfNota,
            formasPagamentoId: pedido.formaPagamento && pedido.formaPagamento.id,
            voucher: pedido.voucher,
            valorVoucher: pedido.valorVoucher,
            produtosAdicionados: produtos.map(({ id }) => id),
        };
    }
    default:
        return state;
    }
};

export default pedidoReducer;
